import type { COUNTRY_SHORTS } from '~/constants'
import { ROUTE_BASE_NAME } from '~/constants'
import { type POINT_OF_INTEREST_TYPE, POINT_OF_INTEREST_COUNTRY_ID } from '~/constants/point-of-interest'
import type { NearbyLocation } from '~/components/general/nearby-locations/types'
import keywordTranslations from '~~/i18n/locales/common/search/keywords.json'
import { POI_MANUFACTURER_KEYWORDS } from '~/constants/keywords'
import type { PointOfInterest, PointOfInterestContentBlocksQuery, PointOfInterestNearbyQuery, PointOfInterestRouteDetails, PointOfInterestSearchQuery } from '~/types/rental-api-aliases'
import type { GRAMMAR_RULE_BITS } from '~/constants/grammar'

type NearbyMode = 'campgrounds' | 'rv-rentals'

export default function usePointOfInterest() {
  const route = useRoute()
  const { $rentals } = useNuxtApp()
  const { locale, t, te } = useI18n({
    useScope: 'global',
    messages: keywordTranslations,
  })
  const localePath = useLocalePath()

  function getKeywordTitle(keyword?: string, args: { pluralize?: boolean, casing?: 'rvTitle' | 'sentence' | 'title', prefix?: 'article' | 'best', suffix?: 'rentals' } = {}) {
    const keywordTranslationKey = `keyword['${keyword}'].label${args?.pluralize ? '_plural' : ''}`

    let prefix = ''
    let suffix = ''

    if (args.prefix) {
      if (te(`keyword['${keyword}'].prefix.${args.prefix}`)) {
        prefix = t(`keyword['${keyword}'].prefix.${args.prefix}`)
      }
      else if (args.prefix === 'article') {
        // Only add fallback article for 'article' prefix type.
        // Other prefix types like 'best' should not get a fallback
        // since surrounding text may already include the article.
        if (args.pluralize) {
          // "the RVs" or "les VRs"
          prefix = locale.value === 'fr' ? 'les' : 'the'
        }
        else {
          // "an RV" or "un VR"
          prefix = locale.value === 'fr' ? 'un' : 'an'
        }
      }
    }

    const defaultTitle = args.pluralize ? t('common.rvs') : t('common.rv')

    const rvTitle = te(keywordTranslationKey) ? t(keywordTranslationKey) : POI_MANUFACTURER_KEYWORDS[keyword as keyof typeof POI_MANUFACTURER_KEYWORDS] || defaultTitle

    if (args.suffix) {
      suffix = args.suffix === 'rentals' ? t('common.rentals') : ''
    }

    const title = [
      prefix,
      args.casing === 'rvTitle' ? capitalizeFirstLetter(rvTitle) : rvTitle,
      suffix,
    ].filter(Boolean).map((x) => args.casing === 'title' ? capitalizeFirstLetter(x) : x).join(' ')

    return args.casing === 'sentence' ? capitalizeFirstLetter(title) : title
  }

  const { priceDisplay } = usePrice()

  function getComponentByKey(breadcrumbs: PointOfInterestRouteDetails['Breadcrumbs'], key?: POINT_OF_INTEREST_TYPE) {
    if (!key) {
      return undefined
    }

    return breadcrumbs?.find((breadcrumb) => breadcrumb.Component === key)
  }

  function templateVariables(
    { variable, args }: { variable: string, args: string[] },
    routeDetails?: PointOfInterestRouteDetails,
  ) {
    const pointOfInterest = routeDetails?.PointOfInterest
    const regionRouteDetails = getComponentByKey(routeDetails?.Breadcrumbs, 'Region')
    const countryRouteDetails = getComponentByKey(routeDetails?.Breadcrumbs, 'Country')
    const regionPoi = regionRouteDetails ?? countryRouteDetails
    const regionContext = regionPoi ?? pointOfInterest
    const regionDisplayName = regionPoi?.Names?.[locale.value] ?? pointOfInterest?.Name
    const poiParentName = typeof pointOfInterest?.ParentAttributes?.Abbreviation === 'string'
      ? pointOfInterest.ParentAttributes.Abbreviation
      : pointOfInterest?.ParentName

    const poiGrammarRules = pointOfInterest?.Attributes?.GrammarRules as GRAMMAR_RULE_BITS | undefined

    const variableMap: Record<string, (args: string[]) => number | string | undefined> = {
      averagePrice: () => priceDisplay({ value: routeDetails?.PointOfInterest?.Stats?.AvgPrice ?? 150, round: true }),

      minimumPrice: () => priceDisplay({ value: routeDetails?.PointOfInterest?.Stats?.MinPrice ?? 20, round: true }),

      keyword: (args) => getKeywordTitle(route.params?.keyword?.toString(), {
        casing: args[0] as 'rvTitle' | 'sentence' | 'title' | undefined,
      }),

      keywordPlural: () => getKeywordTitle(route.params?.keyword?.toString(), {
        pluralize: true,
      }),

      keywordWithArticle: () => getKeywordTitle(route.params?.keyword?.toString(), { prefix: 'article' }),

      locationLatitude: () => pointOfInterest?.Coordinates?.Latitude ?? '',

      locationLongitude: () => pointOfInterest?.Coordinates?.Longitude ?? '',

      locationName: () =>
        pointOfInterest
          ? pointOfInterestLocationName({
              name: pointOfInterest.Name,
              component: pointOfInterest.Component,
              parentName: poiParentName,
            })
          : undefined,

      locationNameWithArticle: (args) =>
        pointOfInterest
          ? pointOfInterestLocationNameWithArticle({
              name: pointOfInterest.Name,
              locale: locale.value,
              capitalize: args[0] === 'true',
              grammarRules: poiGrammarRules,
            })
          : undefined,

      locationNameWithAutoPreposition: (args) =>
        pointOfInterest
          ? pointOfInterestLocationNameWithPreposition({
              locale: locale.value,
              pointOfInterestId: pointOfInterest.Id,
              name: pointOfInterest.Name,
              component: pointOfInterest.Component,
              parentName: poiParentName,
              useNearby: args[0] === 'true',
              grammarRules: poiGrammarRules,
            })
          : undefined,

      locationNameWithFromPreposition: () =>
        pointOfInterest
          ? pointOfInterestLocationNameWithPreposition({
              pointOfInterestId: pointOfInterest.Id,
              name: pointOfInterest.Name,
              component: pointOfInterest.Component,
              parentName: poiParentName,
              preposition: 'from',
              locale: locale.value,
              grammarRules: poiGrammarRules,
            })
          : undefined,

      locationNameWithOfPreposition: () =>
        pointOfInterest
          ? pointOfInterestLocationNameWithPreposition({
              pointOfInterestId: pointOfInterest.Id,
              name: pointOfInterest.Name,
              component: pointOfInterest.Component,
              parentName: poiParentName,
              preposition: 'of',
              locale: locale.value,
              grammarRules: poiGrammarRules,
            })
          : undefined,

      regionName: () =>
        regionContext && regionDisplayName
          ? pointOfInterestLocationName({ name: regionDisplayName })
          : undefined,

      regionNameWithArticle: (args) =>
        regionContext && regionDisplayName
          ? pointOfInterestLocationNameWithArticle({
              name: regionDisplayName,
              grammarRules: regionPoi ? undefined : poiGrammarRules,
              locale: locale.value,
              capitalize: args[0] === 'true',
            })
          : undefined,

      regionNameWithAutoPreposition: (args) =>
        regionContext && regionDisplayName
          ? pointOfInterestLocationNameWithPreposition({
              pointOfInterestId: regionPoi?.Id ?? pointOfInterest?.Id,
              name: regionDisplayName,
              component: regionPoi?.Component,
              locale: locale.value,
              useNearby: args[0] === 'true',
              grammarRules: regionPoi ? undefined : poiGrammarRules,
            })
          : undefined,
    }

    if (!variableMap[variable]) return `{${variable}${args ? `(${args})` : ''}}`

    return variableMap[variable](args)
  }

  function replaceVarsInContent(content: string, routeDetails?: PointOfInterestRouteDetails) {
    const variableMatches = content.match(/\{[^}]+\}/g) || []

    if (!variableMatches.length) {
      return content
    }

    variableMatches.forEach((vm) => {
      const [, variable, args] = vm.replace(/[{}]/g, '').match(/^([^(]+)(?:\(([^)]+)\))?/) || []
      if (!variable) return
      const value = templateVariables({ variable, args: args?.split(',') ?? [] }, routeDetails)
      // Handle undefined/null values to prevent .toString() errors
      if (value === undefined || value === null) {
        // Keep the original template variable if value cannot be resolved
        return
      }
      content = content.replace(vm, value.toString())
    })

    return content
  }

  type routeDetailsAction = 'campgrounds' | 'rv-rental'

  const nearbyQueryParams: Record<routeDetailsAction, Partial<Record<POINT_OF_INTEREST_TYPE, { DistanceKM?: number, PageSize: number, MinimumHubs?: number }>>> = {
    'rv-rental': {
      Country: {
        PageSize: 100,
      },
      Region: {
        PageSize: 20,
        MinimumHubs: 8,
      },
      City: {
        DistanceKM: 250,
        PageSize: 20,
        MinimumHubs: 8,
      },
    },
    'campgrounds': {
      Country: {
        PageSize: 60,
      },
      Region: {
        PageSize: 24,
        MinimumHubs: 4,
      },
      City: {
        DistanceKM: 250,
        PageSize: 24,
        MinimumHubs: 4,
      },
    },
  }

  async function getDestinationDetails({ action, blockContentKeyword }: { action: routeDetailsAction, blockContentKeyword?: string }) {
    const segments = destinationPathSegments(route.params.destinationPath)
    const path = destinationPathString(segments)
    const keyword = Array.isArray(route.params.keyword) ? route.params.keyword.join(' ') : route.params.keyword

    let poiType: POINT_OF_INTEREST_TYPE = 'City'
    if (segments.length === 1) {
      poiType = 'Country'
    }
    else if (segments.length === 2) {
      poiType = 'Region'
    }

    const nearbyParams = nearbyQueryParams[action][poiType]

    const destinationDetailsResponse = await $rentals('/api/point-of-interest/destination-details/{languageCode}', {
      path: {
        languageCode: locale.value,
      },
      query: {
        Path: [path, action, keyword].filter(Boolean).join('/'),
        Faq: true,
        NearbyDistanceKM: nearbyParams?.DistanceKM ?? undefined,
        NearbyPageSize: nearbyParams?.PageSize ?? undefined,
        NearbyMinimumHubs: nearbyParams?.MinimumHubs ?? undefined,
        BlockContentKeyword: blockContentKeyword ?? keyword,
      },
    })

    if (!destinationDetailsResponse) return undefined

    if (destinationDetailsResponse.RouteDetails?.FAQs?.length) {
      destinationDetailsResponse.RouteDetails.FAQs = destinationDetailsResponse.RouteDetails.FAQs.map((faqItem) => ({
        ...faqItem,
        Answer: replaceVarsInContent(faqItem.Answer ?? '', destinationDetailsResponse.RouteDetails ?? undefined),
        Question: replaceVarsInContent(faqItem.Question ?? '', destinationDetailsResponse.RouteDetails ?? undefined),
      }))
    }

    return destinationDetailsResponse
  }

  async function getRouteDetails(action: routeDetailsAction = 'rv-rental') {
    const path = destinationPathString(route.params.destinationPath)
    const keyword = Array.isArray(route.params.keyword) ? route.params.keyword.join(' ') : route.params.keyword

    const routeDetailsResponse = await $rentals('/api/point-of-interest/route/details', {
      query: {
        path: [path, action, keyword].filter(Boolean).join('/'),
        languageCode: locale.value,
        faq: true,
      },
    })

    if (!routeDetailsResponse) return undefined

    if (routeDetailsResponse.FAQs?.length) {
      routeDetailsResponse.FAQs = routeDetailsResponse.FAQs.map((faqItem) => ({
        ...faqItem,
        Answer: replaceVarsInContent(faqItem.Answer ?? '', routeDetailsResponse),
        Question: replaceVarsInContent(faqItem.Question ?? '', routeDetailsResponse),
      }))
    }

    return routeDetailsResponse
  }

  async function getContentBlocks(query: PointOfInterestContentBlocksQuery) {
    const contentBlocks = await $rentals('/api/point-of-interest/content-blocks', {
      query: {
        ...query,
        LanguageCode: locale.value,
      },
    })

    return contentBlocks
  }

  async function getNearby({ pointOfInterestPublicId, pointOfInterestTypeName, query }: { pointOfInterestPublicId: string, pointOfInterestTypeName: POINT_OF_INTEREST_TYPE, query: PointOfInterestNearbyQuery }) {
    const nearby = await $rentals('/api/point-of-interest/nearby/{pointOfInterestPublicId}/{pointOfInterestTypeName}/{languageCode}', {
      path: {
        pointOfInterestPublicId,
        pointOfInterestTypeName,
        languageCode: locale.value,
      },
      query: {
        ...query,
        PointOfInterestId: pointOfInterestPublicId,
      },
    })

    if (!nearby) return nearby

    nearby.Results = nearby.Results?.map((poi) => ({ ...poi, Photos: [] }))

    return nearby
  }

  async function getPOIs(query: PointOfInterestSearchQuery) {
    const pois = await $rentals('/api/point-of-interest/search', {
      query: {
        ...query,
        LanguageCode: locale.value,
      },
    })

    return pois?.Results ?? []
  }

  function buildNearbyLocation(mode: NearbyMode, location: PointOfInterest, keyword?: string): NearbyLocation {
    const isRvRentals = mode === 'rv-rentals'

    const routeName = isRvRentals ? ROUTE_BASE_NAME.rv_rentals_destination : ROUTE_BASE_NAME.campgrounds_destination

    return {
      path: localePath({
        name: routeName,
        params: {
          keyword: isRvRentals ? keyword : undefined,
          destinationPath: destinationPathSegments(location?.CanonicalPath),
        },
      }),
      name: location.Name ?? '',
      shortName: typeof location.ParentAttributes?.Abbreviation === 'string'
        ? `${location.Name}, ${location.ParentAttributes.Abbreviation}`
        : location.Name ?? '',
      fullName: location.FullName ?? '',
      nameWithPreposition: pointOfInterestLocationNameWithPreposition({
        locale: locale.value,
        pointOfInterestId: location.Id,
        name: location.Name,
        component: location.Component,
        parentName: typeof location.ParentAttributes?.Abbreviation === 'string' ? location.ParentAttributes.Abbreviation : location.ParentName,
        grammarRules: location.Attributes?.GrammarRules,
      }),
      distanceKm: location.DistanceKM ?? null,
      rvCount: isRvRentals ? (location.Stats?.RVCount ?? null) : null,
    }
  }

  async function getNearbyRegions(mode: NearbyMode = 'rv-rentals', countryCode?: COUNTRY_SHORTS) {
    const keyword = Array.isArray(route.params.keyword) ? route.params.keyword.join(' ') : route.params.keyword

    const [regionsUS, regionsCA] = await Promise.all([
      !countryCode || countryCode === 'US'
        ? getNearby({
            pointOfInterestPublicId: POINT_OF_INTEREST_COUNTRY_ID.US,
            pointOfInterestTypeName: 'Region',
            query: {
              ParentPointOfInterestId: POINT_OF_INTEREST_COUNTRY_ID.US,
              PageSize: 100,
              Keyword: keyword,
            },
          })
        : undefined,
      !countryCode || countryCode === 'CA'
        ? getNearby({
            pointOfInterestPublicId: POINT_OF_INTEREST_COUNTRY_ID.CA,
            pointOfInterestTypeName: 'Region',
            query: {
              ParentPointOfInterestId: POINT_OF_INTEREST_COUNTRY_ID.CA,
              PageSize: 100,
              Keyword: keyword,
            },
          })
        : undefined,
    ])

    return {
      nearbyUS: regionsUS?.Results?.filter((location) => location.Indexable)?.map((location) => buildNearbyLocation(mode, location, keyword)) ?? [],
      nearbyCA: regionsCA?.Results?.filter((location) => location.Indexable)?.map((location) => buildNearbyLocation(mode, location, keyword)) ?? [],
    }
  }

  return {
    getContentBlocks,
    getDestinationDetails,
    getNearby,
    getPOIs,
    getRouteDetails,
    getNearbyRegions,
    buildNearbyLocation,

    getKeywordTitle,
    getComponentByKey,
  }
}
